import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonGroupModule } from '@progress/kendo-angular-buttons';
import { DataStateChangeEvent, SelectableSettings } from '@progress/kendo-angular-grid';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { TextBoxComponent, TextBoxModule } from '@progress/kendo-angular-inputs';
import { PageChangeEvent, PagerModule } from '@progress/kendo-angular-pager';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { cartIcon, searchIcon } from '@progress/kendo-svg-icons';
import { Observable, Subscription, of, zip } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { IInventoryService } from 'src/app/products/services/inventory.service.interface';
import { IProductService } from 'src/app/products/services/product.service.interface';
import { GridTemplateComponent } from 'src/app/shared/components/grid-template/grid-template.component';
import { LoadingPanelComponent } from 'src/app/shared/components/loading-panel';
import { ProductCardComponent } from 'src/app/shared/components/product-card/product-card.component';
import { ProductDetailDialogComponent } from 'src/app/shared/components/product-detail-dialog/product-detail-dialog.component';
import { selectProductsCardViewFormName, selectProductsLineColumnsFormName } from 'src/app/shared/constants/form-names.consts';
import { LanguageCodes, NameFieldLanguageCodes } from 'src/app/shared/constants/language-codes';
import { PageableOptions } from 'src/app/shared/constants/pageable-options';
import { SalesDistrict } from 'src/app/shared/constants/sales-district';
import { Selectors } from 'src/app/shared/constants/selectors';
import { StringConstants } from 'src/app/shared/constants/string-constants';
import { TextBoxExtensionDirective } from 'src/app/shared/directives/kendo-textbox.directive';
import { CosmosCustomer } from 'src/app/shared/models/customer';
import { FormConfiguration } from 'src/app/shared/models/form-configuration';
import { KendoGridBase } from 'src/app/shared/models/kendo-grid-base';
import { ODataResponse } from 'src/app/shared/models/odata-response';
import { PortalOrder } from 'src/app/shared/models/order-creation/portal-order';
import { ProductWithInventoryAndQuantity } from 'src/app/shared/models/order-creation/product-with-quantity';
import { InventoryOnline, InventoryOnlineStatus } from 'src/app/shared/models/products/inventory';
import { ColumnSettings } from 'src/app/shared/models/ui/column-settings';
import { SecurityService } from 'src/app/shared/security/security.service';
import { ICustomerService } from 'src/app/shared/services/customer/customer.service.interface';
import { FilterAndSortDescriptionService } from 'src/app/shared/services/filter-sort-description.service';
import { KendoAlertService } from 'src/app/shared/services/kendo-alerts.service';
import { CustomLanguageService } from 'src/app/shared/services/language.service';
import { OrderCartService } from 'src/app/shared/services/order-cart.service';
import { IPricelistService } from 'src/app/shared/services/pricelist/pricelist.service.interface';
import { CompanyAdminConfigurationStore } from 'src/app/shared/stores/company-admin-configuration.store';
import { cardViewIcon, listViewIcon } from 'src/assets/icons/viewIcons';
import { selectProductColumns } from './select-product-columns';
import { ISkuFilterData } from './sku-filter/sku-filter/sku-filter-data';
import { SkuFilterComponent } from './sku-filter/sku-filter/sku-filter.component';

@Component({
    templateUrl: './select-products.component.html',
    selector: 'ntw-order-select-products',
    styleUrls: ['./select-product.component.scss', '../../shared/styles/button.scss'],
    imports: [ProductDetailDialogComponent, ButtonGroupModule, CommonModule, TranslateModule, SkuFilterComponent, TextBoxModule,
        SVGIconModule, LoadingPanelComponent, ProductCardComponent, GridTemplateComponent, PagerModule, TextBoxExtensionDirective
    ]
})
export class SelectProductsComponent extends KendoGridBase<ProductWithInventoryAndQuantity> implements OnInit, AfterViewChecked, OnDestroy {
    @Input() portalOrder: PortalOrder;
    @Output() portalOrderChange = new EventEmitter<PortalOrder>();
    @Input() inventoryOnline: InventoryOnline[];

    productDetailDialogOpened = false;
    productDetailDialogProduct: ProductWithInventoryAndQuantity | null = null;

    @ViewChild(GridTemplateComponent) private gridTemplateComponent: GridTemplateComponent;
    @ViewChild(TextBoxComponent) searchBar: TextBoxComponent;

    @Input() searchValue: string;
    @Output() searchValueChange = new EventEmitter<string>();

    skuFilterData?: ISkuFilterData;

    currentAccount: CosmosCustomer;
    customer: CosmosCustomer;
    selectedProducts: ProductWithInventoryAndQuantity[];
    selectedProduct: ProductWithInventoryAndQuantity;
    formConfig: FormConfiguration;
    columnSettings: ColumnSettings[];
    columnSettingsCardView: ColumnSettings[];
    viewType: 'card' | 'list' = 'list';
    pageSizes = PageableOptions.pageSizes;
    totalRecords: number = 0;

    icons = {
        cart: cartIcon,
        search: searchIcon,
        cardView: cardViewIcon,
        listView: listViewIcon
    }

    productsNavigationPath: string = null;

    public selectableSettings: SelectableSettings = {
        mode: 'multiple',
        drag: false,
        enabled: true,
    }

    createFormGroup = (product: ProductWithInventoryAndQuantity) => {
        const cartProduct = this.orderCartService.getProductByAxCode(product.AXCode);
        const quantity = product?.Quantity ?? cartProduct?.quantity ?? 1;

        return new UntypedFormGroup({
            AXCode: new UntypedFormControl(product.AXCode),
            Quantity: new UntypedFormControl(quantity, Validators.pattern("^[1-9][0-9]*")),
        })
    };

    constructor(
        private companyAdminConfigurationStore: CompanyAdminConfigurationStore,
        private customerService: ICustomerService,
        private filterSortService: FilterAndSortDescriptionService,
        private inventoryService: IInventoryService,
        private pricelistService: IPricelistService,
        private kendoAlertService: KendoAlertService,
        private languageService: CustomLanguageService,
        private orderCartService: OrderCartService,
        private productService: IProductService,
        private securityService: SecurityService,
        private translateService: TranslateService,
    ) {
        super();
    }

    private searchSubscription: Subscription;

    ngOnInit(): void {
        this.companyAdminConfigurationStore.getFormConfiguration(selectProductsLineColumnsFormName).pipe(tap(formConfig => {
            this.formConfig = formConfig;
            this.setColumnSettings();
        })).subscribe(() => {
            this.setNameSort(this.kendoGridState);
            this.getListData();
            this.setNameColumn();
            this.translateService.onLangChange.subscribe(() => {
                this.setNameSort(this.kendoGridState);
                this.setNameColumn();
            });
        });

        this.companyAdminConfigurationStore.getFormConfiguration(selectProductsCardViewFormName).subscribe(formConfig => {
            this.columnSettingsCardView = FormConfiguration.applyToColumnSettings(formConfig, JSON.parse(JSON.stringify(selectProductColumns)));
        });


        // Try to get the view type from local storage
        const viewType = localStorage.getItem('orderProcessProductsViewType');
        if (viewType && (viewType === 'card' || viewType === 'list')) {
            this.viewType = viewType as 'card' | 'list';
        }
    }

    public searchBarValueChangeSubscription: Subscription;

    ngAfterViewChecked(): void {
        if (!this.searchBarValueChangeSubscription && this.searchBar) {
            this.searchBarValueChangeSubscription = this.searchBar.valueChange.subscribe(value => {
                this.searchValue = value;
                this.searchValueChange.emit(value as string);
            });
        }
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        if (this.searchBarValueChangeSubscription) {
            this.searchBarValueChangeSubscription.unsubscribe();
        }
    }

    @ViewChild('ntw-product-detail-dialog') productDetailDialog: ProductDetailDialogComponent;

    openProductDetailDialog = (product?: never) => {
        if (product) {
            this.productDetailDialogProduct = product;
        }
        else {
            this.productDetailDialogProduct = this.selectedProduct;
        }
        this.productDetailDialogOpened = true;
    }

    closeProductDetailDialog = () => {
        this.productDetailDialogOpened = false;
    }

    viewTypeChange = (viewType: 'card' | 'list') => {
        if (this.kendoGridState.filter.filters.length > 0) {
            this.kendoGridState.filter.filters = [];
            this.getEntitiesForAccount();
        }
        this.viewType = viewType;
        localStorage.setItem('orderProcessProductsViewType', viewType);
    }

    private getListData() {
        this.securityService.getCurrentAccount().subscribe({
            next: currentAccount => {
                this.currentAccount = currentAccount;
                if (!currentAccount) {
                    this.loading = false;
                    return;
                }
                this.productsNavigationPath = 'account/' + this.currentAccount?.AXCode + '/' + "products";
                if (this.searchValue) {
                    this.onAfterValueChanged(this.searchValue);
                    this.searchValueChange.emit(this.searchValue);
                }
                else {
                    this.getEntitiesForAccount();
                }
            },
            error: error => {
                this.kendoAlertService.showErrorAlert(error);
                this.loading = false;
            }
        });
    }

    productCardPagerPageChange = (event: PageChangeEvent) => {
        this.kendoGridState.skip = event.skip;
        this.kendoGridState.take = event.take;
        this.refreshView();
    }

    private setColumnSettings() {
        if (!this.formConfig.fieldConfigurations.find(x => x.name === 'SalesPrice')) {
            this.formConfig.fieldConfigurations.push({
                name: 'SalesPrice',
                visible: false,
                order: 999
            });
        }

        this.columnSettings = FormConfiguration.applyToColumnSettings(this.formConfig, JSON.parse(JSON.stringify(selectProductColumns)));
    }

    private setNameColumn() {
        this.setColumnSettings();
        const translationNameField = this.getTranslationNameField();
        const i = this.columnSettings.findIndex(x => x.field === 'Name');
        if (translationNameField && i > -1) {
            this.columnSettings[i].field = translationNameField;
        }
        this.kendoGridState.skip = 0;
        this.refreshView();
    }

    onSkuFilterChanged(filterData: ISkuFilterData) {
        this.skuFilterData = filterData;
        this.kendoGridState.skip = 0;
        this.refreshView();
    }

    onUpdateCartClick(): void {
        if (!this.selectedProducts) {
            return;
        }
        else if (this.isAnyQuantityValueInvalid()) {
            this.kendoAlertService.showWarningAlert("orderProcess.products.invalidQuantity", true);
            return;
        }
        else if (this.gridTemplateComponent?.editedEntitiesInformation?.filter(x => x.formGroup.value.Quantity == null).length > 0) {
            this.gridTemplateComponent.editedEntitiesInformation.forEach(e => {
                if (e.formGroup.value.Quantity == null) {
                    e.formGroup.value.Quantity = 1;
                }
            });
        }
        this.gridTemplateComponent.saveEditedEntities();
        this.selectedProducts.forEach(product => this.addProductToCart(product));
        this.kendoAlertService.showSuccessAlert("orderProcess.products.productsAddedToCart", true);
        this.gridTemplateComponent.clearSelection();
        this.entities.forEach(e => e.Quantity = null);
        this.portalOrderChange.emit(this.portalOrder);
    }

    onAddProductToCartClick(product: ProductWithInventoryAndQuantity): void {
        this.addProductToCart(product);
        this.kendoAlertService.showSuccessAlert("orderProcess.products.productAddedToCart", true);
        this.portalOrderChange.emit(this.portalOrder);
    }

    private addProductToCart(product: ProductWithInventoryAndQuantity) {
        this.orderCartService.updateCartWithProduct(product);
        if (!this.inventoryOnline.find(inv => inv.axCode === product.AXCode)) {
            this.inventoryOnline.push({
                axCode: product.AXCode,
                status: InventoryOnlineStatus.Loading
            } as InventoryOnline);
        }
    }

    onViewDetailsClick(product: ProductWithInventoryAndQuantity) {
        this.selectedProduct = product;
        this.openProductDetailDialog();
    }

    isAnyProductSelected(): boolean {
        return this.selectedProducts?.length > 0;
    }
    isAnyQuantityValueInvalid(): boolean {
        return this.gridTemplateComponent?.editedEntitiesInformation?.filter(x => x.formGroup.invalid).length > 0;
    }

    public getSelectedRecords = (selectedRecords: ProductWithInventoryAndQuantity[]) => {
        if (!selectedRecords) {
            return;
        }

        this.selectedProducts = selectedRecords;
    }

    override dataStateChange = (state: DataStateChangeEvent) => {
        this.kendoGridState = state;
        this.getEntitiesForAccount();
    }

    getEntitiesForAccount() {
        this.loading = true;
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        this.searchSubscription = this.customerService.getCustomerByCode(this.currentAccount.AXCode).pipe(
            switchMap(customer => {
                this.customer = customer;
                if (!this.customer?.TikkurilaCompanyRegisterCode) {
                    this.kendoAlertService.showErrorAlert('alerts.axCustomerCompanyRegisterCodeMissing');
                    this.loading = false;
                    return null;
                }
                const stateCopy = JSON.parse(JSON.stringify(this.kendoGridState));
                this.addDefaultFilters(stateCopy);
                this.setNameSort(stateCopy);
                const additionalFilters = this.getAdditionalProductFilters();
                return this.productService.getProductsWithFilters(
                    stateCopy,
                    Selectors.selectProductsView,
                    additionalFilters
                ) as Observable<ODataResponse<ProductWithInventoryAndQuantity[]>>;
            }),
            switchMap(productsResponse => {
                if (!productsResponse || !productsResponse.value || productsResponse.value.length === 0) {
                    return of(productsResponse);
                }
                productsResponse.value.forEach(product => {
                    product.InventoryLoading = true;
                    product.SalesPriceLoading = true;
                    product.UnitNetPriceLoading = true;
                });
                return of(productsResponse);
            }),
            switchMap(productsResponse => {
                if (productsResponse.count === 0 || !productsResponse.value || productsResponse.value.length === 0) {
                    if (this.skuFilterData) {
                        this.kendoAlertService.showWarningAlert("alerts.noProductsFoundSKU", true);
                    }
                    else {
                        this.kendoAlertService.showWarningAlert("alerts.noProductsFound", true);
                    }
                    this.loading = false;
                    return of(productsResponse);
                }

                const $inventory = this.inventoryService.getInventoryForWarehouseByProductsOffline(this.portalOrder.warehouse.bkWarehouseKey, productsResponse.value).pipe(
                    map(inventoriesResponse => {
                        productsResponse.value.forEach(product => {
                            const inventory = inventoriesResponse.find(x => x.bkProductCoverageKey === `${product.AXCode}_${product.DataAreaID}_${this.portalOrder.warehouse.bkWarehouseKey}`);
                            if (inventory) {
                                const stock = inventory?.PhysicalInvent ?? 0;
                                product.Inventory = stock >= 0 ? stock : 0;
                                product.InventoryLoading = false;
                            }
                        });
                        return productsResponse;
                    }),
                    catchError(error => {
                        this.kendoAlertService.showErrorAlert(error);
                        return of(productsResponse);
                    }),
                    finalize(() => {
                        productsResponse.value.forEach(product => {
                            if (product.InventoryLoading) {
                                product.InventoryLoading = false;
                                product.InventoryNotAvailable = true;
                            }
                        });
                    })
                );


                const $pricelist = this.pricelistService.getPriceForMultipleProducts(productsResponse.value, this.customer).pipe(
                    map(pricelistResponse => {
                        productsResponse.value.forEach(product => {
                            const priceItem = pricelistResponse?.items?.find(x => x.id === product.AXCode);
                            if (priceItem && !priceItem.errorMsg) {
                                ProductWithInventoryAndQuantity.calculateProductPrice(product, priceItem);
                                product.SalesPriceLoading = false;
                                product.UnitNetPriceLoading = false;
                            }
                        });
                        return productsResponse;
                    }),
                    catchError(error => {
                        this.kendoAlertService.showErrorAlert(error);
                        return of(productsResponse);
                    }),
                    finalize(() => {
                        productsResponse.value.forEach(product => {
                            if (product.SalesPriceLoading) {
                                product.SalesPriceLoading = false;
                                product.SalesPriceNotAvailable = true;
                            }
                            if (product.UnitNetPriceLoading) {
                                product.UnitNetPriceLoading = false;
                                product.UnitNetPriceNotAvailable = true;
                            }
                        });
                    })
                );

                if (this.columnSettings.some(x => x.field === 'SalesPrice' || x.field === 'UnitNetPrice')) {
                    return zip($inventory, $pricelist).pipe(map(() => productsResponse));
                }
                else {
                    return $inventory.pipe(map(() => productsResponse));
                }
            }))
            .subscribe({
                next: oDataResponse => {
                    this.totalRecords = oDataResponse.count;
                    this.generateGridDataViewOData(oDataResponse);
                },
                error: error => {
                    this.kendoAlertService.showErrorAlert("alerts.errorDuringDataRetrieve");
                    console.error(error);
                    this.loading = false;
                }
            });
    }

    private getAdditionalProductFilters(): CompositeFilterDescriptor | undefined {
        const additionalFilters: CompositeFilterDescriptor = { logic: 'and', filters: [] };
        if (this.searchBarFilters) {
            additionalFilters.filters.push(this.searchBarFilters);
        }
        if (this.skuFilterData) {
            const skuFilter = this.getSkuFilter();
            if (skuFilter) {
                additionalFilters.filters.push(skuFilter);
            }
        }
        return additionalFilters.filters.length > 0 ? additionalFilters : undefined;
    }

    private getSkuFilter(): FilterDescriptor | undefined {
        if (!this.skuFilterData) {
            return undefined;
        }
        const level = this.skuFilterData.level;
        return this.filterSortService.createFilter(`SkuClassificationCode${level + 1}`, this.skuFilterData.code, "eq");
    }

    private setNameSort(state: State) {
        if (state.sort?.length > 0 && state.sort.every(x => !x.field.startsWith('Name'))) {
            return;
        }

        const nameLanguageField = this.getTranslationNameField();
        if (!nameLanguageField) {
            return;
        }

        const nameSort = state.sort?.find(x => x.field.startsWith('Name'));
        const updatedSort = this.filterSortService.createSortDescriptor(nameLanguageField, nameSort?.dir ?? 'asc');

        if (state.sort) {
            state.sort = state.sort.filter(x => !x.field.startsWith('Name'));
            state.sort.push(updatedSort);
        }
        else {
            this.filterSortService.setDefaultSortDescriptor(state, updatedSort);
        }
    }

    private addDefaultFilters(state: State) {
        if (!state.filter) {
            state.filter = { logic: 'and', filters: [] };
        }
        this.setDataAreaFilter(state);
        this.setMasterItemTypeCodeFilter(state);
        this.setReleasedItemStatusFilter(state);
        this.setVisibleInWebShopFilter(state);
        this.setBrandFiltersBySalesDistrict(state);
    }

    private setDataAreaFilter(state: State) {
        const currentAreaId = this.customer.TikkurilaCompanyRegisterCode;
        const areaFilter = this.filterSortService.createFilter(StringConstants.ProductDataAreaField, currentAreaId, "eq");
        this.filterSortService.addFilter(state, areaFilter);
    }

    private setMasterItemTypeCodeFilter(state: State) {
        const masterItemTypeCodeFilter = this.filterSortService.createFilter(StringConstants.MasterItemTypeCodeField, StringConstants.SalesArticlesMasterItemTypeCode, "eq");
        this.filterSortService.addFilter(state, masterItemTypeCodeFilter);
    }

    private setReleasedItemStatusFilter(state: State) {
        const statusFilter = this.filterSortService.createCompositeFilter(StringConstants.ProductReleasedItemLifeCycleAxStatusField,
            [StringConstants.ProductActiveStatusValue, StringConstants.ProductToBeDiscontinuedStatusValue, StringConstants.ProductDiscontinuedStatusValue], "eq", 'or');
        this.filterSortService.addCompositeFilter(state, statusFilter);
    }

    private setVisibleInWebShopFilter(state: State) {
        const visibleInWebShopFilter = this.filterSortService.createFilter(StringConstants.VisibleInWebShopField, StringConstants.VisibleInWebShopYes, "eq");
        this.filterSortService.addFilter(state, visibleInWebShopFilter);
    }

    private setBrandFiltersBySalesDistrict(state: State) {
        if (!this.customer || !this.customer.SalesDistrict) {
            return;
        }
        const salesDistrict = this.customer.SalesDistrict;
        if (salesDistrict.startsWith(SalesDistrict.AlcroSweden)) {
            const brandFilter = this.filterSortService.createCompositeFilter(StringConstants.CommissionGroupField, [StringConstants.CommissionGroupAll, StringConstants.CommissionGroupAlcro, StringConstants.CommissionGroupGoodsForResale], "eq", "or");
            this.filterSortService.addCompositeFilter(state, brandFilter);
        }
        else if (salesDistrict.startsWith(SalesDistrict.BeckersSweden)) {
            const brandFilter = this.filterSortService.createCompositeFilter(StringConstants.CommissionGroupField, [StringConstants.CommissionGroupAll, StringConstants.CommissionGroupBeckers], "eq", "or");
            this.filterSortService.addCompositeFilter(state, brandFilter);
        }
    }

    override refreshView(): void {
        this.getEntitiesForAccount();
    }

    override getQuickSearchFilters(query: string): FilterDescriptor[] {
        const filterArray = [];
        const nameLanguageField = this.getTranslationNameField();
        if (nameLanguageField) {
            filterArray.push(this.filterSortService.createFilter(`${nameLanguageField}_LC`, query.toLocaleLowerCase(), "contains"));
        }
        filterArray.push(this.filterSortService.createFilter(`NameLC`, query.toLocaleLowerCase(), "contains"));
        filterArray.push(this.filterSortService.createFilter("tolower(AXCode)", query.toLocaleLowerCase(), "contains"));
        filterArray.push(this.filterSortService.createFilter("tolower(EANCode)", query.toLocaleLowerCase(), "contains"));
        return filterArray;
    }

    private getTranslationNameField(): string {
        switch (this.languageService.currentLanguage) {
            case LanguageCodes.EnglishUSLanguageCode:
                return NameFieldLanguageCodes.EnglishUSNameField;
            case LanguageCodes.NorwegianLanguageCode:
                return NameFieldLanguageCodes.NorwegianNameField;
            case LanguageCodes.PolishLanguageCode:
                return NameFieldLanguageCodes.PolishNameField;
            case LanguageCodes.SwedishLanguageCode:
                return NameFieldLanguageCodes.SwedishNameField;
            case LanguageCodes.RussianLanguageCode:
                return NameFieldLanguageCodes.RussianNameField;
            default:
                return '';
        }
    }
}
